import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, imgUrl, errMsg } from "@/lib/api";
import { FUELS, GEARBOXES } from "@/components/Filters";
import { AttachmentUploader } from "@/components/Attachments";

const EMPTY = { brand: "", model: "", year: "", km: "", fuel: "Benzina", gearbox: "Manuale", power_cv: "", price: "",
  accept_offers: true, min_offer: "", city: "", description: "", images: [], attachments: [] };
export const MAX_PHOTOS = 60;

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

const PhotoUploader = ({ images, setImages }) => {
  const api = useApi();
  const [busy, setBusy] = useState(null);
  const upload = async (e) => {
    const all = Array.from(e.target.files || []);
    const files = all.slice(0, MAX_PHOTOS - images.length);
    e.target.value = "";
    if (all.length > files.length) toast.warning(`Massimo ${MAX_PHOTOS} foto: ne carico ${files.length}`);
    const paths = [];
    setBusy([0, files.length]);
    try {
      for (let i = 0; i < files.length; i += 5) {
        const batch = await Promise.all(files.slice(i, i + 5).map((file) => {
          const fd = new FormData();
          fd.append("file", file);
          return api("post", "/uploads", fd).then((r) => r.path);
        }));
        paths.push(...batch);
        setBusy([paths.length, files.length]);
      }
    } catch (err) { toast.error(errMsg(err)); } finally {
      setImages([...images, ...paths]);
      setBusy(null);
    }
  };
  return (
    <>
    <p className="text-xs text-slate-500" data-testid="photo-count">{images.length} / {MAX_PHOTOS} foto</p>
    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="photo-uploader">
      {images.map((p, i) => (
        <div key={p} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-100">
          <img src={imgUrl(p)} alt="" className="w-full h-full object-cover" />
          {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] bg-slate-900 text-white px-1.5 py-0.5 rounded">Copertina</span>}
          <button type="button" data-testid={`photo-remove-${i}`} onClick={() => setImages(images.filter((x) => x !== p))}
            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 grid place-items-center"><X size={14} /></button>
        </div>
      ))}
      {images.length < MAX_PHOTOS && (
        <label className="aspect-[4/3] rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 grid place-items-center cursor-pointer text-slate-500 transition-colors duration-200">
          {busy ? <span className="flex flex-col items-center text-xs gap-1"><Loader2 className="animate-spin" />{busy[0]}/{busy[1]}</span> : <span className="flex flex-col items-center text-xs gap-1"><ImagePlus size={20} />Aggiungi foto</span>}
          <input data-testid="photo-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={upload} disabled={!!busy} />
        </label>
      )}
    </div>
    </>
  );
};

export default function CreateListing() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    api("get", "/my/listings").then((list) => {
      const l = list.find((x) => x.id === id);
      if (!l) { toast.error("Annuncio non trovato"); nav("/area-personale"); return; }
      setF({ ...EMPTY, ...l, power_cv: l.power_cv ?? "", min_offer: l.min_offer ?? "" });
    }).catch((e) => toast.error(errMsg(e)));
  }, [id, api, nav]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.images.length) return toast.error("Aggiungi almeno una foto");
    setBusy(true);
    const body = { ...f, year: +f.year, km: +f.km, price: +f.price, power_cv: f.power_cv ? +f.power_cv : null,
      min_offer: f.accept_offers && f.min_offer ? +f.min_offer : null };
    try {
      const r = await api(id ? "put" : "post", id ? `/listings/${id}` : "/listings", body);
      toast.success(id ? "Annuncio aggiornato" : "Annuncio pubblicato");
      nav(`/veicolo/${r.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">{id ? "Modifica annuncio" : "Pubblica un veicolo"}</h1>
      <p className="text-slate-500 mt-1">Scegli se vendere solo a prezzo fisso o accettare anche offerte al ribasso.</p>
      <form onSubmit={submit} className="mt-8 space-y-8" data-testid="create-listing-form">
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading text-lg font-semibold">Foto</h2>
          <PhotoUploader images={f.images} setImages={(images) => setF((p) => ({ ...p, images }))} />
        </section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading text-lg font-semibold">Documenti PDF scaricabili</h2>
          <p className="text-xs text-slate-500">Es. perizia, certificato chilometraggio, garanzia. Gli iscritti approvati potranno scaricarli.</p>
          <AttachmentUploader items={f.attachments || []} setItems={(attachments) => setF((p) => ({ ...p, attachments }))} />
        </section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
          <h2 className="font-heading text-lg font-semibold sm:col-span-2">Dati del veicolo</h2>
          <Field label="Marca"><Input data-testid="create-brand-input" required value={f.brand} onChange={set("brand")} placeholder="Es. Fiat" /></Field>
          <Field label="Modello"><Input data-testid="create-model-input" required value={f.model} onChange={set("model")} placeholder="Es. 500 1.0 Hybrid" /></Field>
          <Field label="Anno di immatricolazione"><Input data-testid="create-year-input" required type="number" value={f.year} onChange={set("year")} /></Field>
          <Field label="Chilometraggio"><Input data-testid="create-km-input" required type="number" value={f.km} onChange={set("km")} /></Field>
          <Field label="Alimentazione">
            <Select value={f.fuel} onValueChange={(v) => setF({ ...f, fuel: v })}>
              <SelectTrigger data-testid="create-fuel-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FUELS.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Cambio">
            <Select value={f.gearbox} onValueChange={(v) => setF({ ...f, gearbox: v })}>
              <SelectTrigger data-testid="create-gearbox-select"><SelectValue /></SelectTrigger>
              <SelectContent>{GEARBOXES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Potenza (CV)"><Input data-testid="create-power-input" type="number" value={f.power_cv} onChange={set("power_cv")} /></Field>
          <Field label="Città"><Input data-testid="create-city-input" required value={f.city} onChange={set("city")} /></Field>
          <div className="sm:col-span-2"><Field label="Descrizione"><Textarea data-testid="create-description-input" rows={5} value={f.description} onChange={set("description")} /></Field></div>
        </section>
        <section className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading text-lg font-semibold">Prezzo e modalità di vendita</h2>
          <Field label="Prezzo Compra subito (€)"><Input data-testid="create-price-input" required type="number" value={f.price} onChange={set("price")} /></Field>
          <div className="flex items-center justify-between p-4 rounded-xl bg-amber-50 border border-amber-200">
            <div>
              <p className="font-semibold text-amber-900">Accetta offerte al ribasso</p>
              <p className="text-xs text-amber-700">Gli acquirenti potranno proporti un prezzo inferiore.</p>
            </div>
            <Switch data-testid="create-accept-offers-switch" checked={f.accept_offers} onCheckedChange={(v) => setF({ ...f, accept_offers: v })} />
          </div>
          {f.accept_offers && (
            <Field label="Offerta minima accettata (€, facoltativa)"><Input data-testid="create-min-offer-input" type="number" value={f.min_offer} onChange={set("min_offer")} /></Field>
          )}
        </section>
        <Button data-testid="create-listing-form-submit" disabled={busy} className="w-full h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-base">
          {busy ? "Pubblicazione..." : id ? "Salva modifiche" : "Pubblica annuncio"}
        </Button>
      </form>
    </div>
  );
}
